'use client';
// Libs
import { ReactElement } from 'react';
import ReactDOM from 'react-dom';
import '@mui/styles';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import { v4 } from 'uuid';
// App
import { defaultTheme } from '../../theme';
import AlertDialog, {
  AlertDialogActions,
  AlertDialogButton,
  AlertDialogButtonType,
} from '../../components/alert-dialog';
import { DialogServiceParam } from './dialog.interfaces';
import { ButtonActions, ButtonActionsLabel } from './dialog.constants';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

export default class DialogService {
  /**
   * Gets the container of the dialog
   * @returns the container dom element
   */
  private static getContainer(id: string) {
    return document.querySelector(`#${id}`);
  }

  /**
   * Creates container of dialog
   */
  private static createContainer(id: string) {
    const container = document.createElement('div');

    container.setAttribute('id', id);
    document.body.appendChild(container);

    return this.getContainer(id);
  }

  /**
   * Destroys the container of dialog
   */
  public static cleanup(id: string): void {
    const container = this.getContainer(id);

    if (container !== null) {
      ReactDOM.unmountComponentAtNode(container);
      container.remove();
    }
  }

  /**
   * Manages component creation and destruction
   * @param content the content of the dialog (dialogProps)
   */
  private static createDialog<ReturnType>(
    dialog: (onClose: (value: ReturnType) => void) => ReactElement,
    theme?: Theme
  ): Promise<ReturnType> {
    const promise: Promise<ReturnType> = new Promise((resolve) => {
      const id = `dialog-${v4()}`;

      const onClose = (value: ReturnType) => {
        this.cleanup(id);
        resolve(value);
      };

      const container = this.createContainer(id);

      ReactDOM.render(
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme || defaultTheme}>{dialog(onClose)}</ThemeProvider>
        </StyledEngineProvider>,
        container
      );
    });

    return promise;
  }

  /**
   * Creates a dialog of type
   * @param title the title of the dialog (dialogProps)
   * @param body the body of the dialog (dialogProps)
   * @param actions the specification for buttons
   * @param theme the app theme
   */
  private static createAlertDialog(
    param: DialogServiceParam,
    defaultActions: AlertDialogActions
  ): Promise<AlertDialogButtonType> {
    const { title, body, actionsOverrides, theme } = param;
    const actions = {
      ...defaultActions,
      ...actionsOverrides,
    };
    const dialog = (onClose: (value: AlertDialogButtonType) => void) => (
      <AlertDialog title={title} actions={actions} body={body} onClose={onClose} />
    );

    return this.createDialog(dialog, theme);
  }

  /**
   * Displays a custom dialog
   * @param dialog the content of the dialog
   * @param theme the app theme
   */
  public static showDialog<ReturnType>(
    dialog: (onClose: (value?: ReturnType) => void) => ReactElement,
    theme?: Theme
  ): Promise<ReturnType | undefined> {
    return this.createDialog(dialog, theme);
  }

  /**
   * Displays a popup with a title, a message, and a ok button
   * @param param the param for dialog creation
   */
  public static showInfo(param: DialogServiceParam): Promise<AlertDialogButtonType> {
    const buttonMiddle: AlertDialogButton = {
      type: ButtonActions.OK,
      label: ButtonActionsLabel.OK,
      color: 'primary',
    };
    const actions: AlertDialogActions = { buttonMiddle };

    return this.createAlertDialog(param, actions);
  }

  /**
   * Displays a popup with a title, a message, an ok and a cancel button
   * @param param the param for dialog creation
   */
  public static showInfoWithCancel(param: DialogServiceParam): Promise<AlertDialogButtonType> {
    const buttonMiddle: AlertDialogButton = {
      type: ButtonActions.OK,
      label: ButtonActionsLabel.OK,
      color: 'secondary',
    };
    const buttonLeft: AlertDialogButton = {
      type: ButtonActions.CANCEL,
      label: ButtonActionsLabel.CANCEL,
      color: 'tertiary',
    };
    const actions: AlertDialogActions = { buttonMiddle, buttonLeft };

    return this.createAlertDialog(param, actions);
  }

  /**
   * Displays a popup with a title, a message, a confirm button, and a cancel button
   * @param param the param for dialog creation
   */
  public static askConfirmCancelQuestion(param: DialogServiceParam): Promise<AlertDialogButtonType> {
    const buttonMiddle: AlertDialogButton = {
      type: ButtonActions.CANCEL,
      label: ButtonActionsLabel.CANCEL,
      color: 'tertiary',
      variant: 'outlined',
    };
    const buttonRight: AlertDialogButton = {
      type: ButtonActions.CONFIRM,
      label: ButtonActionsLabel.CONFIRM,
      color: 'primary',
      variant: 'contained',
    };
    const actions: AlertDialogActions = { buttonMiddle, buttonRight };

    return this.createAlertDialog(param, actions);
  }

  /**
   * Displays a popup with a title, a message, a continue button, and a cancel button
   * @param param the param for dialog creation
   */
  public static askContinueCancelQuestion(param: DialogServiceParam): Promise<AlertDialogButtonType> {
    const buttonMiddle: AlertDialogButton = {
      type: ButtonActions.CANCEL,
      label: ButtonActionsLabel.CANCEL,
      color: 'tertiary',
    };
    const buttonRight: AlertDialogButton = {
      type: ButtonActions.CONTINUE,
      label: ButtonActionsLabel.CONTINUE,
    };
    const actions: AlertDialogActions = { buttonMiddle, buttonRight };

    return this.createAlertDialog(param, actions);
  }

  /**
   * Displays a popup with a title, a message, a yes button, and a no button
   * @param param the param for dialog creation
   */
  public static askYesNoQuestion(param: DialogServiceParam): Promise<AlertDialogButtonType> {
    const buttonMiddle: AlertDialogButton = {
      type: ButtonActions.NO,
      label: ButtonActionsLabel.NO,
      color: 'tertiary',
    };
    const buttonRight: AlertDialogButton = {
      type: ButtonActions.YES,
      label: ButtonActionsLabel.YES,
    };
    const actions: AlertDialogActions = { buttonMiddle, buttonRight };

    return this.createAlertDialog(param, actions);
  }

  /**
   * Displays a popup with a title, a message, a yes button, a no button, and a cancel button
   * @param param the param for dialog creation
   */
  public static askYesNoCancelQuestion(param: DialogServiceParam): Promise<AlertDialogButtonType> {
    const buttonRight: AlertDialogButton = {
      type: ButtonActions.CANCEL,
      label: ButtonActionsLabel.CANCEL,
      color: 'tertiary',
    };
    const buttonLeft: AlertDialogButton = {
      type: ButtonActions.NO,
      label: ButtonActionsLabel.NO,
    };
    const buttonMiddle: AlertDialogButton = {
      type: ButtonActions.YES,
      label: ButtonActionsLabel.YES,
    };
    const actions: AlertDialogActions = {
      buttonLeft,
      buttonMiddle,
      buttonRight,
    };

    return this.createAlertDialog(param, actions);
  }
}
